import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import { createVendorLocation, IVendorLocation } from '@/core/services/vendors'

interface CreateLocationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  onSuccess: () => void
  onError: (message: string) => void
}

interface ILocationValues {
  locations: { value: string }[]
}

const schema = yup.object().shape({
  locations: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string().required('Location name is required'),
      }),
    )
    .min(1, 'Add at least one location')
    .required('Field is required'),
})

export const CreateLocationModal = ({
  open,
  onOpenChange,
  vendorId,
  onSuccess,
  onError,
}: CreateLocationModalProps) => {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
  } = useForm<ILocationValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as any,
    defaultValues: { locations: [{ value: '' }] },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'locations',
  })

  useEffect(() => {
    if (!open) return
    return () => {
      reset({ locations: [{ value: '' }] })
    }
  }, [open])

  const handleClose = () => {
    onOpenChange(false)
  }

  const submit = async (values: ILocationValues) => {
    try {
      await createVendorLocation(
        vendorId,
        values.locations.map((loc) => ({
          id: vendorId,
          name: loc.value,
        })),
      )

      onSuccess()
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="xl"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="Vendor Entity"
            title={
              <span className="flex items-center gap-2">
                Add New <span className="text-accent-1">Location</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button form="create-location-form" type="submit" variant="primary">
            Add New
          </Button>
        </div>
      }
    >
      <form
        id="create-location-form"
        onSubmit={handleSubmit(submit)}
        className="space-y-7"
      >
        <div className="text-2xl font-bold leading-none text-monochrome-1">
          Locations
        </div>

        <div className="flex flex-col gap-3">
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 relative">
              <Input
                isInvalid={!!errors?.locations?.[index]?.value?.message}
                {...register(`locations.${index}.value`)}
                type="text"
                placeholder="Enter Location Name"
                className="flex-1"
              />
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="shrink-0 absolute right-2 rounded-full p-1 text-monochrome-2 hover:text-monochrome-1"
                >
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                  />
                </button>
              )}
            </div>
          ))}

          <div className="flex justify-center">
            <Button
              type="button"
              variant="secondary"
              onClick={() => append({ value: '' })}
            >
              Add New
              <Icon
                icon="plus-square"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
